// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get } from './api';
import { ENDPOINTS } from '../utils/constants';
import type { Artifact, PagedArtifacts } from '../types';

export interface ListSopsFilters {
  artifactType?: string;
  domain?: string;
  equipmentType?: string;
  procedureType?: string;
  riskTier?: string;
  status?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

const FILTER_TO_QUERY_PARAM: Record<keyof ListSopsFilters, string> = {
  artifactType: 'artifact_type',
  domain: 'domain',
  equipmentType: 'equipment_type',
  procedureType: 'procedure_type',
  riskTier: 'risk_tier',
  status: 'status',
  q: 'q',
  page: 'page',
  pageSize: 'page_size',
};

/**
 * List catalog artifacts. Defaults to `artifact_type=sop` so callers get SOPs
 * unless they override it. Extra facets map camelCase → snake_case query params.
 */
export const listSops = (filters: ListSopsFilters = {}): Promise<PagedArtifacts> => {
  const merged: ListSopsFilters = { artifactType: 'sop', ...filters };
  const params = new URLSearchParams();
  (Object.keys(merged) as (keyof ListSopsFilters)[]).forEach(key => {
    const value = merged[key];
    if (value !== undefined && value !== null && value !== '') {
      params.set(FILTER_TO_QUERY_PARAM[key], String(value));
    }
  });
  const qs = params.toString();
  const base = ENDPOINTS.SOP_FACTORY_ARTIFACTS;
  return get<PagedArtifacts>(qs ? `${base}?${qs}` : base);
};

/** Fetch a single artifact (SOP or policy) by its stable id. */
export const getArtifact = (id: string): Promise<Artifact | null> =>
  get<Artifact | null>(ENDPOINTS.SOP_FACTORY_ARTIFACT_BY_ID(id));
